import React from "react";
import { withTheme, withStyles } from "@material-ui/core/styles";
import { Paper, Grid, Typography, Divider } from "@material-ui/core";
import {
  formatMessage,
  withTooltip,
  FormattedMessage,
  PublishedComponent,
  FormPanel,
  TextInput,
  Contributions,
  withModulesManager,
} from "@openimis/fe-core";

const styles = (theme) => ({
  paper: theme.paper.paper,
  tableTitle: theme.table.title,
  item: theme.paper.item,
  paperHeaderAction: { padding: theme.spacing(1) },
  fullHeight: { height: "100%" },
});

import { DEFAULT, INSUREE_ACTIVE_STRING } from "../constants";

const INSUREE_INSUREE_CONTRIBUTION_KEY = "insuree.Insuree";
const INSUREE_INSUREE_PANELS_CONTRIBUTION_KEY = "insuree.Insuree.panels";

class InsureeMasterPanel extends FormPanel {
  constructor(props) {
    super(props);
    this.isInsureeStatusRequired = props.modulesManager.getConf(
      "fe-insuree",
      "insureeForm.isInsureeStatusRequired",
      false
    );
    this.renderLastNameFirst = props.modulesManager.getConf(
      "fe-insuree",
      "renderLastNameFirst",
      DEFAULT.RENDER_LAST_NAME_FIRST
    );
    this.isPhotoRequired = props.modulesManager.getConf(
      "fe-insuree",
      "insureeForm.isPhotoRequired",
      false
    );
  }

  // Force FormPanel to always detect changes
  isDirty = () => true;

  updateSingleField = (key, value) => {
    this.updateAttributes({ ...this.props.edited, [key]: value });
  };

  updateMultipleFields = (values) => {
    this.updateAttributes({ ...this.props.edited, ...values });
  };

  renderLastNameField = (edited, classes, readOnly) => (
    <Grid item xs={3} className={classes.item}>
      <TextInput
        module="insuree"
        label="Insuree.lastName"
        required
        readOnly={readOnly}
        value={edited?.lastName || ""}
        onChange={(v) =>
          this.updateSingleField("lastName", v?.target ? v.target.value : v)
        }
      />
    </Grid>
  );

  renderMiddleNameField = (edited, classes, readOnly) => (
    <Grid item xs={3} className={classes.item}>
      <TextInput
        module="insuree"
        label="Insuree.middleName"
        required
        readOnly={readOnly}
        value={edited?.middleName || ""}
        onChange={(v) =>
          this.updateSingleField("middleName", v?.target ? v.target.value : v)
        }
      />
    </Grid>
  );

  renderGivenNameField = (edited, classes, readOnly) => (
    <Grid item xs={3} className={classes.item}>
      <TextInput
        module="insuree"
        label="Insuree.otherNames"
        required
        readOnly={readOnly}
        value={edited?.otherNames || ""}
        onChange={(v) =>
          this.updateSingleField("otherNames", v?.target ? v.target.value : v)
        }
      />
    </Grid>
  );

  render() {
    const {
      intl,
      classes,
      edited = {},
      title = "Insuree.title",
      titleParams = { label: "" },
      readOnly = false, // default to editable
      actions,
      editedId,
    } = this.props;

    // initialize required fields if empty
    const safeEdited = {
      lastName: edited.lastName || "",
      otherNames: edited.otherNames || "",
      dob: edited.dob || null,
      gender: edited.gender || { code: "" },
      status: edited.status || INSUREE_ACTIVE_STRING,
      ...edited,
    };

    return (
      <Grid container>
        <Grid item xs={12}>
          <Paper className={classes.paper}>
            <Grid container className={classes.tableTitle}>
              <Grid item xs={3} container alignItems="center" className={classes.item}>
                <Typography variant="h5">
                  <FormattedMessage module="insuree" id={title} values={titleParams} />
                </Typography>
              </Grid>
              <Grid item xs={9}>
                <Grid container justifyContent="flex-end">
                  {!!safeEdited?.family?.headInsuree &&
                    safeEdited.family.headInsuree.id !== safeEdited.id && (
                      <Grid item xs={3}>
                        <PublishedComponent
                          pubRef="insuree.RelationPicker"
                          withNull
                          nullLabel={formatMessage(intl, "insuree", "Relation.none")}
                          readOnly={readOnly}
                          value={safeEdited?.relationship?.id || ""}
                          onChange={(v) =>
                            this.updateSingleField("relationship", { id: v })
                          }
                        />
                      </Grid>
                    )}
                  {!!actions &&
                    actions.map((a, idx) => (
                      <Grid item key={`form-action-${idx}`} className={classes.paperHeaderAction}>
                        {withTooltip(a.button, a.tooltip)}
                      </Grid>
                    ))}
                </Grid>
              </Grid>
            </Grid>
            <Divider />

            {/* ROW 1: Insurance Number and Names */}
            <Grid container className={classes.item}>
              <Grid item xs={3} className={classes.item}>
                <PublishedComponent
                  pubRef="insuree.InsureeNumberInput"
                  module="insuree"
                  label="Insuree.chfId"
                  required
                  readOnly={readOnly}
                  value={safeEdited?.chfId || ""}
                  editedId={editedId}
                  onChange={(v) => this.updateSingleField("chfId", v)}
                />
              </Grid>

              {this.renderLastNameFirst ? (
                <>
                  {this.renderLastNameField(safeEdited, classes, readOnly)}
                  {this.renderMiddleNameField(safeEdited, classes, readOnly)}
                  {this.renderGivenNameField(safeEdited, classes, readOnly)}
                </>
              ) : (
                <>
                  {this.renderGivenNameField(safeEdited, classes, readOnly)}
                  {this.renderMiddleNameField(safeEdited, classes, readOnly)}
                  {this.renderLastNameField(safeEdited, classes, readOnly)}
                </>
              )}
            </Grid>

            {/* ROW 2: Other Fields and Photo */}
            <Grid container className={classes.item}>
              <Grid item xs={8}>
                <Grid container>
                  {/* Date of Birth */}
                  <Grid item xs={3} className={classes.item}>
                    <PublishedComponent
                      pubRef="core.DatePicker"
                      value={safeEdited?.dob}
                      module="insuree"
                      label="Insuree.dob"
                      readOnly={readOnly}
                      required
                      maxDate={new Date()}
                      onChange={(v) => this.updateSingleField("dob", v)}
                    />
                  </Grid>

                  {/* Gender */}
                  <Grid item xs={3} className={classes.item}>
                    <PublishedComponent
                      pubRef="insuree.InsureeGenderPicker"
                      value={safeEdited?.gender?.code || ""}
                      module="insuree"
                      readOnly={readOnly}
                      withNull={false}
                      required
                      onChange={(v) => this.updateSingleField("gender", { code: v })}
                    />
                  </Grid>

                  {/* Marital Status */}
                  <Grid item xs={3} className={classes.item}>
                    <PublishedComponent
                      pubRef="insuree.InsureeMaritalStatusPicker"
                      value={safeEdited?.marital || ""}
                      module="insuree"
                      readOnly={readOnly}
                      withNull={false}
                      onChange={(v) => this.updateSingleField("marital", v)}
                    />
                  </Grid>

                  {/* Address */}
                  <Grid item xs={12} className={classes.item}>
                    <PublishedComponent
                      pubRef="insuree.InsureeAddress"
                      value={safeEdited}
                      module="insuree"
                      readOnly={readOnly}
                      onChangeLocation={(v) =>
                        this.updateSingleField("currentVillage", v)
                      }
                      onChangeAddress={(v) =>
                        this.updateSingleField("currentAddress", v)
                      }
                    />
                  </Grid>

                  {/* Contact Info */}
                  <Grid item xs={6} className={classes.item}>
                    <TextInput
                      module="insuree"
                      label="Insuree.phone"
                      readOnly={readOnly}
                      value={safeEdited?.phone || ""}
                      onChange={(v) =>
                        this.updateSingleField("phone", v?.target ? v.target.value : v)
                      }
                    />
                  </Grid>

                  {/* Profession, Education, TypeOfId, Passport */}
                  <Grid item xs={3} className={classes.item}>
                    <PublishedComponent
                      pubRef="insuree.ProfessionPicker"
                      module="insuree"
                      value={safeEdited?.profession?.id || null}
                      readOnly={readOnly}
                      withNull={false}
                      onChange={(v) => this.updateSingleField("profession", { id: v })}
                    />
                  </Grid>
                  {/* <Grid item xs={3} className={classes.item}>
                    <PublishedComponent
                      pubRef="insuree.EducationPicker"
                      module="insuree"
                      value={safeEdited?.education?.id || ""}
                      readOnly={readOnly}
                      withNull={false}
                      onChange={(v) => this.updateSingleField("education", { id: v })}
                    />
                  </Grid> */}
                  <Grid item xs={3} className={classes.item}>
                    <PublishedComponent
                      pubRef="insuree.IdentificationTypePicker"
                      module="insuree"
                      value={safeEdited?.typeOfId?.code || null}
                      readOnly={readOnly}
                      withNull={false}
                      onChange={(v) => this.updateSingleField("typeOfId", { code: v })}
                    />
                  </Grid>
                  <Grid item xs={3} className={classes.item}>
                    <TextInput
                      module="insuree"
                      label="Insuree.passport"
                      readOnly={readOnly}
                      value={safeEdited?.passport || ""}
                      onChange={(v) =>
                        this.updateSingleField("passport", v?.target ? v.target.value : v)
                      }
                    />
                  </Grid>

                  {/* Status Fields */}
                  <Grid item xs={3} className={classes.item}>
                    <PublishedComponent
                      pubRef="insuree.InsureeStatusPicker"
                      label="Insuree.status"
                      value={safeEdited?.status || ""}
                      module="insuree"
                      readOnly={!safeEdited?.uuid || readOnly}
                      required={this.isInsureeStatusRequired}
                      withNull={false}
                      onChange={(v) =>
                        this.updateMultipleFields({
                          status: v,
                          statusReason:
                            v !== INSUREE_ACTIVE_STRING ? null : safeEdited?.statusReason,
                        })
                      }
                    />
                  </Grid>
                </Grid>
              </Grid>

              {/* Right side - Photo */}
              <Grid item xs={4} className={classes.item}>
                <PublishedComponent
                  pubRef="insuree.Avatar"
                  photo={safeEdited?.photo || null}
                  readOnly={readOnly}
                  required={this.isPhotoRequired}
                  withMeta
                  onChange={(v) => this.updateSingleField("photo", v || null)}
                />
              </Grid>
            </Grid>

            {/* Contributions */}
            <Grid container className={classes.item}>
              <Contributions
                {...this.props}
                updateAttribute={this.updateSingleField}
                contributionKey={INSUREE_INSUREE_CONTRIBUTION_KEY}
              />
            </Grid>
          </Paper>

          <Contributions
            {...this.props}
            updateAttribute={this.updateSingleField}
            contributionKey={INSUREE_INSUREE_PANELS_CONTRIBUTION_KEY}
          />
        </Grid>
      </Grid>
    );
  }
}

export default withModulesManager(withTheme(withStyles(styles)(InsureeMasterPanel)));
